import {
  isAllAccountsSelection,
  RiskControlTabs,
} from '@moment/risk-controls-feature/risk-control-tabs'
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

interface RiskControlsState {
  config: RiskControlsConfig
}

/**
 * On the risk-controls page a missing account selection also means the
 * All-accounts view. Kept separate from isAllAccountsSelection: callers like
 * the restrictions tab pass raw account ids where "missing" must mean "none".
 */
export function isAllAccountsView(
  selectedAccount: string | null | undefined,
): boolean {
  return !selectedAccount || isAllAccountsSelection(selectedAccount)
}

interface RiskControlsConfig {
  selectedScope: RiskControlTabs
  selectedOrganization: string | null
  selectedAccount: string | null
  selectedRiskGroup: string | null
  searchQuery: string | null
  editingControlId: string | null
}

const initialState: RiskControlsState = {
  config: {
    selectedScope: RiskControlTabs.Global,
    selectedOrganization: null,
    selectedAccount: null,
    selectedRiskGroup: null,
    searchQuery: null,
    editingControlId: null,
  },
}

const riskControlsSlice = createSlice({
  name: 'riskControls',
  initialState,
  reducers: {
    setSelectedScope: (state, action: PayloadAction<RiskControlTabs>) => {
      state.config.selectedScope = action.payload
    },
    setSelectedOrganization: (state, action: PayloadAction<string>) => {
      state.config.selectedOrganization = action.payload
    },
    setSelectedAccount: (state, action: PayloadAction<string>) => {
      state.config.selectedAccount = action.payload
    },
    setSelectedRiskGroup: (state, action: PayloadAction<string>) => {
      state.config.selectedRiskGroup = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string | null>) => {
      state.config.searchQuery = action.payload
    },
    setEditingControlId: (state, action: PayloadAction<string | null>) => {
      state.config.editingControlId = action.payload
    },
    clearEditingControlId: (state) => {
      state.config.editingControlId = null
    },
  },
})

export const {
  setSelectedScope,
  setSelectedOrganization,
  setSelectedAccount,
  setSelectedRiskGroup,
  setSearchQuery,
  setEditingControlId,
  clearEditingControlId,
} = riskControlsSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default riskControlsSlice
